import { useState } from 'react';

const useImageOptimizer = (options = {}) => {
  const {
    maxInitialSize = 5, // MB
    maxFinalSize = 1, // MB
    maxWidth = 400,
    maxHeight = 400,
    initialQuality = 0.8,
    qualityStep = 0.1,
    minQuality = 0.1,
    sizeReductionFactor = 0.8
  } = options;

  // Spécifications pour les logos de tickets de caisse
  const receiptLogoSpecs = {
    maxWidth: 384, // Largeur standard pour imprimantes 80mm
    maxHeight: 150,
    maxSize: 100 * 1024, // 100KB
    dpi: 203, // DPI standard pour imprimantes thermiques
    format: 'image/png'
  };

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Fonction pour compresser l'image avec une qualité donnée
  const compressImage = (canvas, quality) => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    });
  };

  // Fonction pour vérifier la taille
  const checkSize = (base64String) => {
    const sizeInMB = (base64String.length * 0.75) / (1024 * 1024);
    return sizeInMB <= maxFinalSize;
  };

  // Fonction récursive pour réduire la qualité jusqu'à obtenir une taille acceptable
  const optimizeWithQuality = async (canvas, img, quality) => {
    const compressedImage = await compressImage(canvas, quality);
    
    if (checkSize(compressedImage)) {
      return compressedImage;
    }

    if (quality <= minQuality) {
      // Si la qualité est déjà très basse, réduire encore la taille
      canvas.width = Math.floor(canvas.width * sizeReductionFactor);
      canvas.height = Math.floor(canvas.height * sizeReductionFactor);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return optimizeWithQuality(canvas, img, initialQuality); // Réessayer avec une qualité moyenne
    }

    // Réduire la qualité et réessayer
    return optimizeWithQuality(canvas, img, quality - qualityStep);
  };

  // Fonction pour vérifier les spécifications du logo de ticket
  const validateReceiptLogo = (file) => {
    const warnings = [];
    
    if (file.size > receiptLogoSpecs.maxSize) {
      warnings.push(`Le logo est trop lourd (${(file.size / 1024).toFixed(1)}KB). Taille recommandée : < 100KB`);
    }
    
    if (!file.type.includes('png') && !file.type.includes('bmp')) {
      warnings.push("Format recommandé : PNG ou BMP pour une meilleure qualité d'impression");
    }

    return warnings;
  };

  // Fonction pour optimiser spécifiquement pour les tickets de caisse
  const optimizeReceiptLogo = async (file) => {
    setIsOptimizing(true);
    setError(null);
    setWarnings([]);

    try {
      const logoWarnings = validateReceiptLogo(file);
      if (logoWarnings.length > 0) {
        setWarnings(logoWarnings);
      }

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculer les nouvelles dimensions en respectant les proportions
            if (width > receiptLogoSpecs.maxWidth) {
              height *= receiptLogoSpecs.maxWidth / width;
              width = receiptLogoSpecs.maxWidth;
            }
            if (height > receiptLogoSpecs.maxHeight) {
              width *= receiptLogoSpecs.maxHeight / height;
              height = receiptLogoSpecs.maxHeight;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convertir en PNG avec fond transparent
            canvas.toBlob((blob) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result);
              };
              reader.readAsDataURL(blob);
            }, receiptLogoSpecs.format);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsOptimizing(false);
    }
  };

  // Fonction principale d'optimisation
  const optimizeImage = async (file) => {
    setIsOptimizing(true);
    setError(null);

    try {
      // Vérifier la taille initiale
      if (file.size > maxInitialSize * 1024 * 1024) {
        throw new Error(`L'image est trop grande. Taille maximale : ${maxInitialSize}MB`);
      }

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        throw new Error('Veuillez sélectionner une image valide');
      }

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            // Créer un canvas pour redimensionner l'image
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculer les nouvelles dimensions
            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;

            // Dessiner l'image redimensionnée
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Commencer l'optimisation avec la qualité initiale
            optimizeWithQuality(canvas, img, initialQuality)
              .then(finalImage => {
                if (finalImage) {
                  resolve(finalImage);
                } else {
                  throw new Error('Impossible d\'optimiser l\'image. Veuillez choisir une image plus petite.');
                }
              })
              .catch(error => {
                throw error;
              });
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsOptimizing(false);
    }
  };

  return {
    optimizeImage,
    optimizeReceiptLogo,
    isOptimizing,
    error,
    warnings
  };
};

export default useImageOptimizer; 